/*

  # Notify JSON Endpoint

  A controller to notify a JSON endpoint of an event or request,
  typically a button click, and relay the endpoint's message
  using the response data's "message" key back to the
  user via a dedicated message element.

  This controller depends on thunder::tech's standard response
  JSON format in which "message" is a user-readable string
  and "code" is a string of either "200" or "500".

  Configuration keys are:

  - endpoint - The endpoint to request.
  - once - Set to true to limit the number of successful requests
    to one.
  - networkErrorMessage - A default error message in case of
    being unable to reach the server.
  - body - The object to send as the request body as a JSON
    string. This is provided as a JSON literal, not as an
    encoded string.

  ## Button and message selection

  Add hooks for the controlled element and/or its descendants
  with:

  - notifyJSONEndpoint.button to cause an element to notify
    when clicked
  - notifyJSONEndpoint.message to cause an element display
    the message once the server responds

  ## Class states

  The controlled element and its descendants support class
  state attributes of:

  - data-endpoint-not-notified - classes to remove once
    a successful response is receieved.
  - data-endpoint-notified - classes to add once a
    successful response is receieved.

  These should be used together when they are used, even if
  one of them has an empty value.

  ## Example:

      <button type="button" data-hook="notifyJSONEndpoint notifyJSONEndpoint.button" class="hover:underline cursor-pointer"
        data-endpoint-not-notified="hover:underline cursor-pointer"
        data-endpoint-notified="cursor-default">
        <script type="application/json"
          data-hook="notifyJSONEndpoint.configuration">
          {
            "endpoint": "../endpoint-examples/notification-endpoint.txt",
            "once": true,
            "networkErrorMessage": "Could not reach the server, please try again",
            "body": {
              "key": "value"
            }
          }
        </script>
        <span data-hook="notifyJSONEndpoint.message">Notify</span>
      </button>
*/

import { assignController, select, selectAlso, click, setClassState, unsetClassState } from '../api/element-manipulation'
import { endpointFetch } from '../api/standard-endpoints'

assignController('notifyJSONEndpoint', (element) => {

  const buttons = selectAlso(element, '[data-hook~="notifyJSONEndpoint.button"]')
  const messages = selectAlso(element, '[data-hook~="notifyJSONEndpoint.message"]')
  const configurationElement = select(element, '[data-hook~="notifyJSONEndpoint.configuration"]')
  const configuration = JSON.parse(configurationElement.textContent)
  const stateElements = selectAlso(element, '[data-endpoint-notified]')

  configurationElement.parentNode.removeChild(configurationElement)

  const onButtonClick = event => {
    if ( (controller.countCompleted > 0) && configuration.once ) {
      return
    }
    notify()
  }

  const notify = async () => {
    const response = await endpointFetch(configuration.endpoint, {
      method:'POST',
      body: JSON.stringify(configuration.body),
      headers: {
        'Content-Type': 'application/json',
      },
    }, configuration.networkErrorMessage)
    messages.forEach(message => message.textContent = response.message)
    if ( response.code === '200' ) {
      controller.countCompleted = controller.countCompleted + 1
    }
    stateElements.forEach(stateElement => {
      unsetClassState(stateElement, stateElement.dataset.endpointNotNotified)
      setClassState(stateElement, stateElement.dataset.endpointNotified)
    })
  }

  const controller = {
    notify,
    buttons,
    messages,
    countCompleted: 0
  }

  buttons.forEach(button => click(button, onButtonClick))

  return controller
})
